/** XeAgents.ai**/
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Search,
  Phone,
  ShoppingCart,
  Check,
  Trash2,
  CreditCard,
  Link as LinkIcon,
  Smartphone,
  Globe,
  MapPin,
  Hash,
  AlertCircle,
  Crown,
  RefreshCw,
} from "lucide-react";
import { AuthStorage } from "@/lib/auth-storage";
import { DataPagination, usePagination } from "@/components/ui/data-pagination";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AddressManagement, AddressSelector } from "@/components/AddressManagement";

// Default monthly credits - will be overridden by API value
const DEFAULT_MONTHLY_CREDITS = 50;

interface PublicSettings {
  phone_number_monthly_credits: number;
  low_credits_threshold: number;
  credits_per_minute: number;
}

interface PhoneNumber {
  id: string;
  phoneNumber: string;
  twilioSid: string;
  friendlyName?: string;
  country: string;
  capabilities?: any;
  status: string;
  purchasedAt: string;
  isSystemPool?: boolean;
}

interface AvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  locality?: string;
  region?: string;
  capabilities: {
    voice: boolean;
    sms: boolean;
    mms: boolean;
  };
  provider?: string;
}

interface SearchResponse {
  numbers: AvailableNumber[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
    hasMore: boolean;
  };
  provider?: string;
}

interface IncomingConnection {
  id: string;
  phoneNumberId: string;
  agentId: string;
  agent: {
    name: string;
  };
}

interface TwilioCountry {
  id: string;
  code: string;
  name: string;
  dialCode: string;
  isActive: boolean;
  sortOrder: number;
}

interface User {
  id: string;
  email: string;
  planType: string;
  credits: number;
  organizationCountry?: string;
  billingCountry?: string;
}

export default function PhoneNumbers() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [searchCountry, setSearchCountry] = useState("");
  const [searchAreaCode, setSearchAreaCode] = useState("");
  const [searchPostalCode, setSearchPostalCode] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [searchRegion, setSearchRegion] = useState("");
  const [searchType, setSearchType] = useState<
    "areaCode" | "postalCode" | "location"
  >("areaCode");
  const [selectedNumber, setSelectedNumber] = useState<AvailableNumber | null>(
    null
  );
  const [friendlyName, setFriendlyName] = useState("");
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [numberToRelease, setNumberToRelease] = useState<PhoneNumber | null>(
    null
  );
  const [availableNumbersPage, setAvailableNumbersPage] = useState(1);
  const [availableNumbersLimit, setAvailableNumbersLimit] = useState(20);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [selectedAddressSid, setSelectedAddressSid] = useState<string>("");

  // Countries that require address for regulatory compliance
  const countriesRequiringAddress = ["AU", "GB", "CA"];
  const isAddressRequired = countriesRequiringAddress.includes(searchCountry);

  // Fetch public settings for dynamic monthly credits value
  const { data: publicSettings } = useQuery<PublicSettings>({
    queryKey: ["/api/settings/public"],
  });

  // Use dynamic monthly credits from settings, fallback to default
  const MONTHLY_CREDITS =
    publicSettings?.phone_number_monthly_credits || DEFAULT_MONTHLY_CREDITS;

  const { data: ownedNumbersRaw = [], isLoading: ownedLoading } = useQuery<
    PhoneNumber[]
  >({
    queryKey: ["/api/phone-numbers"],
  });

  // Fetch current user data (including organizationCountry)
  const { data: currentUser } = useQuery<User>({
    queryKey: ["/api/auth/me"],
  });

  // Fetch user's effective limits from the server (handles country-specific plans correctly)
  const { data: userLimits } = useQuery<{
    planName: string;
    planDisplayName: string;
    phoneNumbers: { current: number; max: number; remaining: number; source: string };
  }>({
    queryKey: ["/api/user/limits"],
  });

  // Sort owned numbers by purchase date in descending order (newest first)
  const ownedNumbers = [...ownedNumbersRaw].sort((a, b) => {
    return new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime();
  });

  // Use server-calculated limits (handles country-specific plans correctly)
  const maxPhoneNumbers = userLimits?.phoneNumbers?.max ?? 0;
  const planDisplayName = userLimits?.planDisplayName || 'Free';

  const ownedNonSystemNumbers = ownedNumbers.filter(n => !n.isSystemPool);
  const hasReachedLimit = ownedNonSystemNumbers.length >= maxPhoneNumbers;

  const { data: allCountries = [], isLoading: countriesLoading } = useQuery<
    TwilioCountry[]
  >({
    queryKey: ["/api/twilio-countries"],
  });

  // Priority countries to show at the top of the list
  const priorityCountries = ["US", "GB", "CA", "AU", "IN", "AE", "DE", "FR", "IE", "NZ", "SG", "ZA", "PH"];

  // Add India manually if not in Twilio list (India uses Plivo instead of Twilio)
  const countriesWithIndia = allCountries.some(c => c.code === "IN")
    ? allCountries
    : [...allCountries, { id: "india", code: "IN", name: "India" }];

  // Show all countries, with priority countries at the top
  const countries = [...countriesWithIndia].sort((a, b) => {
    const indexA = priorityCountries.indexOf(a.code);
    const indexB = priorityCountries.indexOf(b.code);

    // Priority countries come first
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // Then alphabetically by name
    return a.name.localeCompare(b.name);
  });

  useEffect(() => {
    if (countries.length > 0 && !searchCountry) {
      const usCountry = countries.find((c) => c.code === "US");
      setSearchCountry(usCountry ? "US" : countries[0].code);
    }
  }, [countries, searchCountry]);

  const { data: incomingData } = useQuery<{
    connections: IncomingConnection[];
    availablePhoneNumbers: PhoneNumber[];
  }>({
    queryKey: ["/api/incoming-connections"],
  });

  const connections = incomingData?.connections || [];

  const getConnection = (phoneNumberId: string) => {
    return connections.find((c) => c.phoneNumberId === phoneNumberId);
  };

  const isCountryValid = countries.some((c) => c.code === searchCountry);

  const canSearch = () => {
    if (!searchCountry || !isCountryValid) return false;
    // Allow country-only searches now
    if (searchType === "areaCode") return true; // Can search with just country
    if (searchType === "postalCode") return true; // Can search with just country
    if (searchType === "location") return true; // Can search with just country
    return false;
  };

  const buildSearchQuery = () => {
    const params = new URLSearchParams();
    if (searchCountry && isCountryValid) {
      params.append("country", searchCountry);
    }
    if (searchType === "areaCode" && searchAreaCode) {
      params.append("areaCode", searchAreaCode);
    } else if (searchType === "postalCode" && searchPostalCode) {
      params.append("postalCode", searchPostalCode);
    } else if (searchType === "location") {
      if (searchCity) params.append("locality", searchCity);
      if (searchRegion) params.append("region", searchRegion);
    }
    // Add pagination
    params.append("page", availableNumbersPage.toString());
    params.append("limit", availableNumbersLimit.toString());
    return params.toString();
  };

  const {
    data: searchResponse,
    isLoading: searchLoading,
    refetch: searchNumbers,
  } = useQuery<SearchResponse>({
    queryKey: [
      "/api/phone-numbers/search",
      searchCountry,
      searchType,
      searchAreaCode,
      searchPostalCode,
      searchCity,
      searchRegion,
      availableNumbersPage,
      availableNumbersLimit,
    ],
    queryFn: async () => {
      if (!canSearch())
        return {
          numbers: [],
          pagination: {
            page: 1,
            limit: 20,
            totalItems: 0,
            totalPages: 0,
            hasMore: false,
          },
        };
      const headers: Record<string, string> = {};
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers["Authorization"] = authHeader;
      }
      const endpoint = `/api/phone-numbers/search?${buildSearchQuery()}`;
      const res = await fetch(endpoint, { headers });
      if (!res.ok) throw new Error(t("phoneNumbers.errors.searchFailed"));
      return res.json();
    },
    enabled: canSearch() && !countriesLoading,
  });

  // Show all available numbers (Twilio for international, Plivo for India)
  const availableNumbers = searchResponse?.numbers || [];
  
  const pagination = searchResponse?.pagination || {
    page: 1,
    limit: 20,
    totalItems: 0,
    totalPages: 0,
    hasMore: false,
  };

  // Reset to page 1 when country changes (query will auto-refetch due to queryKey change)
  useEffect(() => {
    if (searchCountry && isCountryValid && !countriesLoading) {
      setAvailableNumbersPage(1);
    }
  }, [searchCountry, isCountryValid, countriesLoading]);

  const buyMutation = useMutation({
    mutationFn: async ({
      phoneNumber,
      friendlyName,
      addressSid,
    }: {
      phoneNumber: string;
      friendlyName?: string;
      addressSid?: string;
    }) => {
      const res = await apiRequest("POST", "/api/phone-numbers/buy", {
        phoneNumber,
        friendlyName,
        country: searchCountry,
        addressSid: addressSid || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      // Invalidate ALL phone number queries (including those used by Agents page)
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/api/phone-numbers');
        },
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/incoming-connections"],
      });
      // Also invalidate user limits to reflect updated phone count
      queryClient.invalidateQueries({ queryKey: ["/api/user/limits"] });
      setBuyDialogOpen(false);
      setSelectedNumber(null);
      setFriendlyName("");
      setSearchAreaCode("");
      setSearchPostalCode("");
      setSearchCity("");
      setSearchRegion("");
      setSearchType("areaCode");
      toast({
        title: t("phoneNumbers.toast.purchaseSuccess"),
        description: t("phoneNumbers.toast.creditsDeducted", {
          credits: MONTHLY_CREDITS,
        }),
      });
    },
    onError: (error: any) => {
      toast({
        title: t("phoneNumbers.toast.purchaseFailed"),
        description: error.message || t("common.tryAgain"),
        variant: "destructive",
      });
    },
  });

  const releaseMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/phone-numbers/${id}`);
      return res.json();
    },
    onSuccess: async () => {
      // Force immediate refetch of phone numbers to update UI
      await queryClient.refetchQueries({
        queryKey: ["/api/phone-numbers"],
        exact: true,
      });
      // Invalidate ALL phone number queries (including those used by Agents page)
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/api/phone-numbers');
        },
        refetchType: 'all',
      });
      await queryClient.invalidateQueries({
        queryKey: ["/api/incoming-connections"],
      });
      // Also invalidate user limits to reflect updated phone count
      await queryClient.invalidateQueries({ queryKey: ["/api/user/limits"] });
      setReleaseDialogOpen(false);
      setNumberToRelease(null);
      toast({
        title: "Phone Number Deleted",
        description: "You will no longer be charged for this number.",
      });
    },
    onError: (error: any) => {
      setReleaseDialogOpen(false);
      setNumberToRelease(null);

      // Check if the error is because the number is connected to an agent
      if (error.requiresDisconnect || error.message?.includes('connected to an agent')) {
        toast({
          title: "Cannot Release Phone Number",
          description: error.message || `This phone number is connected to an agent. Please disconnect it first.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: t("phoneNumbers.toast.releaseFailed"),
          description: error.message || t("common.tryAgain"),
          variant: "destructive",
        });
      }
    },
  });

  const syncPlivoAppMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/phone-numbers/${id}/sync-plivo-app`);
      return res.json();
    },
    onSuccess: (data) => {
      // Invalidate ALL phone number queries (including those used by Agents page)
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/api/phone-numbers');
        },
        refetchType: 'all',
      });
      toast({
        title: "App Synced",
        description: data.message || "Phone number synced for incoming call routing via Plivo",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync phone number for incoming call routing",
        variant: "destructive",
      });
    },
  });

  const handleBuyNumber = () => {
    if (!selectedNumber) return;
    
    buyMutation.mutate({
      phoneNumber: selectedNumber.phoneNumber,
      friendlyName: friendlyName || undefined,
      addressSid: selectedAddressSid || undefined,
    });
  };

  const handleReleaseNumber = () => {
    if (!numberToRelease) return;
    releaseMutation.mutate(numberToRelease.id);
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`;
    }
    return phone;
  };

  const totalNumbers = ownedNumbers.length;
  const activeNumbers = ownedNumbers.filter((n) => n.status === "active").length;
  const connectedNumbers = connections.length;

  const {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    paginatedItems: paginatedNumbers,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(ownedNumbers, 9);

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 via-green-100/50 to-teal-50 dark:from-emerald-950/40 dark:via-green-900/30 dark:to-teal-950/40 border border-emerald-100 dark:border-emerald-900/50 p-6 md:p-8">
        <div className="absolute inset-0 bg-grid-slate-200/50 dark:bg-grid-slate-700/20 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.5))]" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <Smartphone className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {t("phoneNumbers.title")}
              </h1>
              <p className="text-muted-foreground mt-0.5">
                {t("phoneNumbers.subtitle")}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setLocation("/app/incoming-connections")}
              className="bg-white/80 dark:bg-white/10 border-emerald-200 dark:border-emerald-800"
              data-testid="button-manage-connections"
            >
              <LinkIcon className="h-4 w-4 mr-2" />
              {t("phoneNumbers.manageConnections")}
            </Button>
            <Button
              onClick={() => setBuyDialogOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="button-buy-number"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("phoneNumbers.actions.buyNumber")}
            </Button>
          </div>
        </div>

        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-emerald-100/50 dark:border-emerald-800/30">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <div
                className="text-2xl font-bold text-emerald-700 dark:text-emerald-300"
                data-testid="text-total-numbers"
              >
                {totalNumbers}
              </div>
            </div>
            <div className="text-emerald-600/70 dark:text-emerald-400/70 text-sm">
              {t("phoneNumbers.stats.totalNumbers")}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-green-100/50 dark:border-green-800/30">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                {activeNumbers}
              </div>
            </div>
            <div className="text-green-600/70 dark:text-green-400/70 text-sm">
              {t("common.active")}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-teal-100/50 dark:border-teal-800/30">
            <div className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              <div className="text-2xl font-bold text-teal-700 dark:text-teal-300">
                {connectedNumbers}
              </div>
            </div>
            <div className="text-teal-600/70 dark:text-teal-400/70 text-sm">
              {t("phoneNumbers.stats.connected")}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-100/50 dark:border-cyan-800/30">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
              <div className="text-2xl font-bold text-cyan-700 dark:text-cyan-300">
                {MONTHLY_CREDITS}
              </div>
            </div>
            <div className="text-cyan-600/70 dark:text-cyan-400/70 text-sm">
              {t("phoneNumbers.stats.creditsPerMonth")}
            </div>
          </div>
        </div>
      </div>

      {hasReachedLimit && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20">
          <div className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-1">
                {planDisplayName} Plan Limit Reached
              </h3>
              <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                You have reached the maximum of {maxPhoneNumbers} phone number{maxPhoneNumbers > 1 ? 's' : ''} allowed on the {planDisplayName} plan. To purchase additional phone numbers, please upgrade your plan.
              </p>
              <Button
                onClick={() => setLocation("/app/upgrade")}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade Plan
              </Button>
            </div>
          </div>
        </Card>
      )}

      {ownedLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-6 bg-muted rounded w-3/4 mb-4" />
              <div className="h-4 bg-muted rounded w-full mb-2" />
              <div className="h-4 bg-muted rounded w-2/3" />
            </Card>
          ))}
        </div>
      ) : ownedNumbers.length === 0 ? (
        <Card className="p-8 sm:p-16 text-center">
          <Phone className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">
            {t("phoneNumbers.empty.title")}
          </h3>
          <p className="text-muted-foreground mb-4">
            {t("phoneNumbers.empty.description")}
          </p>
          <Button onClick={() => setBuyDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("phoneNumbers.empty.buyFirst")}
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {paginatedNumbers.map((number) => (
              <Card
                key={number.id}
                className="p-4 sm:p-6"
                data-testid={`card-phone-${number.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-4">
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-base sm:text-lg font-semibold font-mono mb-1 break-all"
                      data-testid="text-phone-number"
                    >
                      {formatPhoneNumber(number.phoneNumber)}
                    </h3>
                    {number.friendlyName && (
                      <p className="text-sm text-muted-foreground truncate">
                        {number.friendlyName}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <Badge
                      variant={
                        number.status === "active" ? "default" : "secondary"
                      }
                    >
                      {number.status === "active"
                        ? t("common.active")
                        : number.status}
                    </Badge>
                    {number.phoneNumber.startsWith('+91') && number.status === 'synced' && (
                      <Badge
                        variant="outline"
                        className="border-green-300 text-green-700 bg-green-50 dark:border-green-700 dark:text-green-400 dark:bg-green-950/30"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Synced
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t("phoneNumbers.labels.country")}:</span>
                    <span className="font-medium text-foreground">
                      {number.country}
                    </span>
                  </div>
                  {!number.isSystemPool && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("phoneNumbers.labels.monthlyCost")}:</span>
                      <span className="font-medium text-foreground">
                        {MONTHLY_CREDITS} {t("phoneNumbers.labels.credits")}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const connection = getConnection(number.id);
                    return connection ? (
                      <div
                        className="flex flex-wrap items-center gap-1 sm:gap-2 pt-3 text-sm"
                        data-testid={`connection-status-connected-${number.id}`}
                      >
                        <LinkIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <span className="text-muted-foreground">
                          {t("phoneNumbers.status.connectedTo")}
                        </span>
                        <span
                          className="font-medium text-foreground truncate"
                          data-testid={`connection-agent-name-${number.id}`}
                        >
                          {connection.agent.name}
                        </span>
                      </div>
                    ) : (
                      <div
                        className="flex items-center gap-2 pt-3 text-sm text-muted-foreground"
                        data-testid={`connection-status-not-connected-${number.id}`}
                      >
                        <span>{t("phoneNumbers.status.notConnected")}</span>
                      </div>
                    );
                  })()}
                </div>

                {!number.isSystemPool && (
                  <div className="pt-4 mt-4 border-t flex gap-2">
                    {number.phoneNumber.startsWith('+91') && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950/30 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => syncPlivoAppMutation.mutate(number.id)}
                        disabled={syncPlivoAppMutation.isPending || number.status === 'synced'}
                        title={number.status === 'synced' ? "Number is already synced for call routing" : "Sync this number for incoming call routing via Plivo"}
                        data-testid={`button-sync-plivo-${number.id}`}
                      >
                        {number.status === 'synced' ? (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Synced
                          </>
                        ) : (
                          <>
                            <RefreshCw className={`h-4 w-4 mr-2 ${syncPlivoAppMutation.isPending ? 'animate-spin' : ''}`} />
                            {syncPlivoAppMutation.isPending ? "Syncing..." : "Sync App"}
                          </>
                        )}
                      </Button>
                    )}
                    {(() => {
                      const isConnected = !!getConnection(number.id);
                      return isConnected ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className={`${number.phoneNumber.startsWith('+91') ? "flex-1" : "w-full"} border-muted-foreground/30 text-muted-foreground cursor-not-allowed`}
                          disabled={true}
                          title="Disconnect this number from the agent first before deleting"
                          data-testid={`button-release-${number.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      ) : (
                        <Button
                          variant="destructive"
                          size="sm"
                          className={number.phoneNumber.startsWith('+91') ? "flex-1" : "w-full"}
                          onClick={() => {
                            setNumberToRelease(number);
                            setReleaseDialogOpen(true);
                          }}
                          title="Delete this phone number to stop monthly charges"
                          data-testid={`button-release-${number.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      );
                    })()}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[9, 18, 27, 54]}
              data-testid="pagination-phone-numbers"
            />
          )}
        </div>
      )}

      <Dialog open={buyDialogOpen} onOpenChange={setBuyDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("phoneNumbers.dialog.buyTitle")}</DialogTitle>
            <DialogDescription>
              {t("phoneNumbers.dialog.buyDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-accent/50 border border-accent rounded-lg p-4 flex items-start gap-3">
            <CreditCard className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-sm mb-1">
                {t("phoneNumbers.dialog.monthlyBilling")}
              </h4>
              <p className="text-sm text-muted-foreground">
                {t("phoneNumbers.dialog.monthlyBillingDesc", {
                  credits: MONTHLY_CREDITS,
                })}
              </p>
            </div>
          </div>

          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <Label>{t("phoneNumbers.labels.country")}</Label>
              <Select
                value={searchCountry}
                onValueChange={setSearchCountry}
                disabled={countriesLoading}
              >
                <SelectTrigger data-testid="select-country">
                  <SelectValue
                    placeholder={
                      countriesLoading
                        ? t("phoneNumbers.placeholders.loadingCountries")
                        : t("phoneNumbers.placeholders.selectCountry")
                    }
                  />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      <Globe className="h-4 w-4 mr-2 inline-block" />
                      {country.name} ({country.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label>{t("phoneNumbers.labels.searchMethod")}</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={searchType === "areaCode" ? "default" : "outline"}
                  className="flex items-center gap-2"
                  onClick={() => setSearchType("areaCode")}
                  data-testid="btn-search-area-code"
                >
                  <Hash className="h-4 w-4" />
                  {t("phoneNumbers.searchTypes.areaCode")}
                </Button>
                <Button
                  type="button"
                  variant={searchType === "postalCode" ? "default" : "outline"}
                  className="flex items-center gap-2"
                  onClick={() => setSearchType("postalCode")}
                  data-testid="btn-search-postal-code"
                >
                  <MapPin className="h-4 w-4" />
                  {t("phoneNumbers.searchTypes.postalCode")}
                </Button>
                <Button
                  type="button"
                  variant={searchType === "location" ? "default" : "outline"}
                  className="flex items-center gap-2"
                  onClick={() => setSearchType("location")}
                  data-testid="btn-search-location"
                >
                  <Globe className="h-4 w-4" />
                  {t("phoneNumbers.searchTypes.cityRegion")}
                </Button>
              </div>
            </div>

            {searchType === "areaCode" && (
              <div className="space-y-2">
                <Label htmlFor="area-code">
                  {t("phoneNumbers.labels.areaCode")}
                </Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="area-code"
                    placeholder={t("phoneNumbers.placeholders.areaCode")}
                    value={searchAreaCode}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 3);
                      setSearchAreaCode(value);
                    }}
                    className="pl-10"
                    data-testid="input-area-code"
                    maxLength={3}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("phoneNumbers.hints.areaCode")}
                </p>
              </div>
            )}

            {searchType === "postalCode" && (
              <div className="space-y-2">
                <Label htmlFor="postal-code">
                  {t("phoneNumbers.labels.postalCode")}
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="postal-code"
                    placeholder={t("phoneNumbers.placeholders.postalCode")}
                    value={searchPostalCode}
                    onChange={(e) =>
                      setSearchPostalCode(e.target.value.slice(0, 10))
                    }
                    className="pl-10"
                    data-testid="input-postal-code"
                    maxLength={10}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("phoneNumbers.hints.postalCode")}
                </p>
              </div>
            )}

            {searchType === "location" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="city">{t("phoneNumbers.labels.city")}</Label>
                  <Input
                    id="city"
                    placeholder={t("phoneNumbers.placeholders.city")}
                    value={searchCity}
                    onChange={(e) => setSearchCity(e.target.value)}
                    data-testid="input-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">
                    {t("phoneNumbers.labels.region")}
                  </Label>
                  <Input
                    id="region"
                    placeholder={t("phoneNumbers.placeholders.region")}
                    value={searchRegion}
                    onChange={(e) =>
                      setSearchRegion(e.target.value.toUpperCase().slice(0, 2))
                    }
                    data-testid="input-region"
                    maxLength={2}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("phoneNumbers.hints.location")}
                </p>
              </div>
            )}

            <Button
              onClick={() => {
                setAvailableNumbersPage(1);
                searchNumbers();
              }}
              disabled={!canSearch() || searchLoading}
              className="w-full"
              data-testid="button-search-numbers"
            >
              {searchLoading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  {t("phoneNumbers.actions.searching")}
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  {t("phoneNumbers.actions.searchNumbers")}
                </>
              )}
            </Button>

            {searchLoading && (
              <div className="text-center py-8 text-muted-foreground">
                <div className="animate-spin h-6 w-6 border-2 border-current border-t-transparent rounded-full mx-auto mb-2" />
                {t("phoneNumbers.actions.searching")}
              </div>
            )}

            {!searchLoading && canSearch() && availableNumbers.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {t("phoneNumbers.search.noResults")}
              </div>
            )}

            {availableNumbers.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{t("phoneNumbers.labels.availableNumbers")}</Label>
                  <span className="text-sm text-muted-foreground">
                    {pagination.totalItems > 0
                      ? `${pagination.totalItems} ${t("phoneNumbers.search.available") || "available"}`
                      : ""}
                  </span>
                </div>
                <div className="border rounded-md divide-y max-h-96 overflow-y-auto">
                  {availableNumbers.map((number) => (
                    <div
                      key={number.phoneNumber}
                      className={`p-4 hover-elevate cursor-pointer transition-colors ${
                        selectedNumber?.phoneNumber === number.phoneNumber
                          ? "bg-accent"
                          : "hover:bg-accent/50"
                      }`}
                      onClick={() => setSelectedNumber(number)}
                      data-testid={`available-number-${number.phoneNumber}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-mono font-semibold">
                            {formatPhoneNumber(number.phoneNumber)}
                          </div>
                          {number.locality && number.region && (
                            <div className="text-sm text-muted-foreground">
                              {number.locality}, {number.region}
                            </div>
                          )}
                        </div>
                        {selectedNumber?.phoneNumber === number.phoneNumber && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <div className="text-sm text-muted-foreground">
                      {t("phoneNumbers.search.pageInfo", {
                        page: pagination.page,
                        totalPages: pagination.totalPages,
                        totalItems: pagination.totalItems,
                      }) ||
                        `Page ${pagination.page} of ${pagination.totalPages} (${pagination.totalItems} total)`}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setAvailableNumbersPage((prev) =>
                            Math.max(1, prev - 1)
                          );
                        }}
                        disabled={pagination.page === 1 || searchLoading}
                      >
                        {t("common.previous")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setAvailableNumbersPage((prev) =>
                            Math.min(pagination.totalPages, prev + 1)
                          );
                        }}
                        disabled={
                          pagination.page >= pagination.totalPages ||
                          searchLoading
                        }
                      >
                        {t("common.next")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedNumber && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="friendly-name">
                    {t("phoneNumbers.labels.friendlyName")}
                  </Label>
                  <Input
                    id="friendly-name"
                    placeholder={t("phoneNumbers.placeholders.friendlyName")}
                    value={friendlyName}
                    onChange={(e) => setFriendlyName(e.target.value)}
                    data-testid="input-friendly-name"
                  />
                </div>

                {isAddressRequired && (
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <div className="flex gap-2">
                      <div className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5">
                        ⚠️
                      </div>
                      <div className="text-sm text-amber-900 dark:text-amber-100">
                        <p className="font-medium">Address Required</p>
                        <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5">
                          {searchCountry} requires a registered address for regulatory compliance. 
                          You must select or create an address before purchasing.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <AddressSelector
                  value={selectedAddressSid}
                  onValueChange={setSelectedAddressSid}
                  onManageClick={() => setAddressDialogOpen(true)}
                  required={isAddressRequired}
                  defaultCountry={searchCountry}
                />
              </>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setBuyDialogOpen(false);
                setSelectedNumber(null);
                setFriendlyName("");
                setSearchAreaCode("");
                setSearchPostalCode("");
                setSearchCity("");
                setSearchRegion("");
                setSearchType("areaCode");
                setAvailableNumbersPage(1);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleBuyNumber}
              disabled={
                !selectedNumber || 
                buyMutation.isPending ||
                (isAddressRequired && !selectedAddressSid)
              }
              data-testid="button-confirm-purchase"
            >
              {buyMutation.isPending ? (
                t("phoneNumbers.actions.purchasing")
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {isAddressRequired && !selectedAddressSid
                    ? "Select Address to Continue"
                    : "Buy Phone Number"}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={releaseDialogOpen} onOpenChange={setReleaseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete Phone Number
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  {numberToRelease ? formatPhoneNumber(numberToRelease.phoneNumber) : ""}
                </span>
                ? This action cannot be undone.
              </p>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3 text-amber-800 dark:text-amber-200 text-sm">
                <p className="font-medium">What happens when you delete:</p>
                <ul className="list-disc list-inside mt-1 space-y-1 text-amber-700 dark:text-amber-300">
                  <li>You will no longer be charged {MONTHLY_CREDITS} credits/month for this number</li>
                  <li>The phone number will be released back to the provider</li>
                  <li>You cannot undo this action - the number may be assigned to someone else</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={releaseMutation.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReleaseNumber}
              disabled={releaseMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="button-confirm-release"
            >
              {releaseMutation.isPending ? "Deleting..." : "Delete Number"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddressManagement
        open={addressDialogOpen}
        onOpenChange={setAddressDialogOpen}
        defaultCountry={searchCountry}
      />
    </div>
  );
}
